import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ArrowRight, ArrowRightCircle } from 'lucide-react'
import api from '../../Includes/api/api'

const NEAR_DAYS = 7

function isoDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function listFrom(payload) {
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload)) return payload
  return []
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

function VisitTable({ rows, loading, emptyTitle, dateLabel, dateOf, areaOf, personOf, extraOf }) {
  if (loading) {
    return <div className="loading"><div className="spinner" /><span>Loading...</span></div>
  }
  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <Calendar size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
        <h3>{emptyTitle}</h3>
        <p>Nothing due in the next {NEAR_DAYS} days</p>
      </div>
    )
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>{dateLabel}</th>
          <th>Area / Company</th>
          <th>Assigned to</th>
          <th>{extraOf ? 'Purpose' : 'Status'}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{formatDate(dateOf(row))}</td>
            <td style={{ fontWeight: 600 }}>{(areaOf(row) || '').split(',')[0].trim() || '—'}</td>
            <td>{personOf(row) || '—'}</td>
            <td>
              {extraOf ? (
                extraOf(row) || '—'
              ) : (
                <span className="badge badge-planned">Pending</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default function Dashboard() {
  const [persons, setPersons] = useState([])
  const [plannedVisits, setPlannedVisits] = useState([])
  const [followups, setFollowups] = useState([])
  const [visitedAreasCount, setVisitedAreasCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [academicYearName, setAcademicYearName] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const yearsRes = await api.get('/basic/academicyear/')
        const years = listFrom(yearsRes.data)
        const activeYear = years.find(y => y.is_active) || years[0]
        const yearId = activeYear?.id
        setAcademicYearName(activeYear?.name || '')
        const yearParams = yearId ? { academic_year_id: yearId } : {}
        const today = new Date()
        const until = new Date()
        until.setDate(today.getDate() + NEAR_DAYS)
        const from = isoDate(today)
        const to = isoDate(until)

        const [personsRes, plannedRes, followupRes, visitedRes] = await Promise.all([
          api.get('/persons/'),
          api.get('/visits/', {
            params: {
              ...yearParams,
              status: 'planned',
              planned_date_from: from,
              planned_date_to: to,
              page_size: 200,
            },
          }),
          api.get('/visits/followups/', {
            params: {
              ...yearParams,
              followup_date_from: from,
              followup_date_to: to,
            },
          }),
          api.get('/visits/', { params: { ...yearParams, status: 'visited', page_size: 1 } }),
        ])
        const planned = listFrom(plannedRes.data).filter(
          (visit) => !(visit.notes || '').trim().toLowerCase().startsWith('follow-up')
        )
        planned.sort((a, b) => String(a.planned_date || '').localeCompare(String(b.planned_date || '')))
        const upcomingFollowups = listFrom(followupRes.data)
        upcomingFollowups.sort((a, b) => String(a.followup_date || '').localeCompare(String(b.followup_date || '')))
        setPersons(listFrom(personsRes.data))
        setPlannedVisits(planned)
        setFollowups(upcomingFollowups)
        setVisitedAreasCount(visitedRes.data.count ?? listFrom(visitedRes.data).length)
      } catch (err) {
        console.error('Dashboard fetch error:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  return (
    <div style={{ paddingTop: '24px' }}>
      {academicYearName && (
        <p className="page-subtitle" style={{ marginBottom: 16 }}>
          Academic year {academicYearName}
        </p>
      )}
      <div className="stats-grid">
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/sales/my-visits?status=planned')}>
          <div>
            <div className="stat-title">Planned visits (next {NEAR_DAYS} days)</div>
            <div className="stat-value orange">
              {loading ? '—' : plannedVisits.length}
            </div>
          </div>
          <a href="#" className="stat-footer-link" onClick={(e) => { e.preventDefault(); navigate('/sales/my-visits?status=planned'); }}>
            Open visits list <ArrowRightCircle size={14} />
          </a>
        </div>

        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/sales/my-visits?status=followups')}>
          <div>
            <div className="stat-title">Follow-up visits (next {NEAR_DAYS} days)</div>
            <div className="stat-value blue">
              {loading ? '—' : followups.length}
            </div>
          </div>
          <a href="#" className="stat-footer-link" onClick={(e) => { e.preventDefault(); navigate('/sales/my-visits?status=followups'); }}>
            Open follow-ups <ArrowRightCircle size={14} />
          </a>
        </div>

        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/sales/visits-map')}>
          <div>
            <div className="stat-title">Visited Areas</div>
            <div className="stat-value red">{loading ? '—' : visitedAreasCount}</div>
          </div>
          <a href="#" className="stat-footer-link" onClick={(e) => { e.preventDefault(); navigate('/sales/visits-map'); }}>
            Open visits map <ArrowRightCircle size={14} />
          </a>
        </div>

        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/sales/persons')}>
          <div>
            <div className="stat-title">Total Personnel</div>
            <div className="stat-value blue">{loading ? '—' : persons.length}</div>
          </div>
          <a href="#" className="stat-footer-link" onClick={(e) => { e.preventDefault(); navigate('/sales/persons'); }}>
            Open expenses <ArrowRightCircle size={14} />
          </a>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            <Calendar size={18} />
            Planned visits near
          </h2>
          <a href="#" className="stat-footer-link" onClick={(e) => { e.preventDefault(); navigate('/sales/my-visits?status=planned'); }}>
            View all <ArrowRight size={14} />
          </a>
        </div>
        <div className="table-container">
          <VisitTable
            rows={plannedVisits}
            loading={loading}
            emptyTitle="No planned visits nearby"
            dateLabel="Planned date"
            dateOf={(visit) => visit.planned_date}
            areaOf={(visit) => visit.area_name}
            personOf={(visit) => visit.person_detail?.name}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            <Calendar size={18} />
            Follow-up visits near
          </h2>
          <a href="#" className="stat-footer-link" onClick={(e) => { e.preventDefault(); navigate('/sales/my-visits?status=followups'); }}>
            View all <ArrowRight size={14} />
          </a>
        </div>
        <div className="table-container">
          <VisitTable
            rows={followups}
            loading={loading}
            emptyTitle="No follow-up visits nearby"
            dateLabel="Follow-up date"
            dateOf={(item) => item.followup_date}
            areaOf={(item) => item.visit_detail?.area_name}
            personOf={(item) => item.visit_detail?.person_detail?.name}
            extraOf={(item) => item.next_purpose}
          />
        </div>
      </div>
    </div>
  )
}
